import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

export interface Discrepancy {
  shiftDate: string;
  shiftType: string;
  expectedAmount: string;
  actualAmount: string;
  difference: string;
}

export interface AuditReport {
  auditId: string;
  periodStart: string;
  periodEnd: string;
  totalExpectedPay: string;
  totalActualPay: string;
  totalDiscrepancy: string;
  discrepancies: Discrepancy[];
  hasDiscrepancies: boolean;
  createdAt: string;
}

export type AuditReportStatus = 'loading' | 'ready' | 'error';

interface UseAuditReportResult {
  report: AuditReport | null;
  status: AuditReportStatus;
  error: string | null;
}

const POLL_INTERVAL_MS = 2500;
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export function useAuditReport(auditId: string | undefined): UseAuditReportResult {
  const [report, setReport] = useState<AuditReport | null>(null);
  const [status, setStatus] = useState<AuditReportStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchReport = useCallback(async () => {
    if (!auditId) {
      setStatus('error');
      setError('No audit ID provided');
      return;
    }

    try {
      abortControllerRef.current = new AbortController();

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        setStatus('error');
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/audit/${auditId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: abortControllerRef.current.signal,
      });

      if (response.status === 404 || response.status === 202) {
        // Report not ready yet, continue polling
        pollingRef.current = setTimeout(fetchReport, POLL_INTERVAL_MS);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        setStatus('error');
        setError(errorData?.error?.message ?? `Failed to fetch report (${response.status})`);
        return;
      }

      const data: AuditReport = await response.json();
      setReport(data);
      setStatus('ready');
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setStatus('error');
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    }
  }, [auditId]);

  useEffect(() => {
    setStatus('loading');
    setReport(null);
    setError(null);
    fetchReport();

    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [fetchReport]);

  return { report, status, error };
}
